import { apiErrorMessage } from "./apiErrors";

/**
 * Wecrew Copilot AI API client — talks to FastAPI backend at /api/v1
 * Dev: Vite proxies /api → http://localhost:8000
 */

const API_BASE = import.meta.env.VITE_API_BASE || "/api/v1";
const TOKEN_KEY = "argusai_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export async function request(path, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = apiErrorMessage(body, detail);
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  health: () => fetch("/health").then((r) => r.json()).catch(() =>
    fetch(`${API_BASE.replace(/\/api\/v1$/, "")}/health`).then((r) => r.json())
  ),

  register: (email, password, full_name) =>
    request("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, name: full_name }),
    }),

  login: async (email, password) => {
    const data = await request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if (data?.access_token) setToken(data.access_token);
    return data;
  },

  me: () => request("/auth/me"),
  listUsers: () => request("/users"),
  listAuditLogs: () => request("/audit-logs?limit=100"),
  listConversations: () => request("/chat/conversations"),
  getConversation: (id) => request(`/chat/conversations/${encodeURIComponent(id)}`),
  deleteConversation: (id) => request(`/chat/conversations/${encodeURIComponent(id)}`, { method: "DELETE" }),

  listKnowledgeBases: () => request("/knowledge-bases"),

  createKnowledgeBase: (payload) =>
    request("/knowledge-bases", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  listDocuments: (kb_id) =>
    request(kb_id ? `/documents?kb_id=${encodeURIComponent(kb_id)}` : "/documents"),

  getChatPolicy: () => request("/chat/policy"),
  updateChatPolicy: (general_enabled) => request("/chat/policy", { method: "PUT", body: JSON.stringify({ general_enabled }) }),

  chatQuery: ({ query, mode = "workspace", kb_ids = [], top_k = 5, conversation_history = [], conversation_id = null }) =>
    request("/chat/query", {
      method: "POST",
      body: JSON.stringify({ query, mode, kb_ids, top_k, conversation_history, conversation_id }),
    }),

  /**
   * Stream RAG tokens via SSE (POST /chat/stream).
   */
  chatStream: async ({
    query,
    mode = "workspace",
    kb_ids = [],
    top_k = 5,
    conversation_history = [],
    conversation_id = null,
    onToken,
    onSources,
    onDone,
  }) => {
    const headers = { "Content-Type": "application/json" };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/chat/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify({ query, mode, kb_ids, top_k, conversation_history, conversation_id }),
    });

    if (!res.ok) {
      let detail = res.statusText;
      try {
        const body = await res.json();
        detail = apiErrorMessage(body, detail);
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }

    const reader = res.body?.getReader();
    if (!reader) throw new Error("Streaming not supported");

    const decoder = new TextDecoder();
    let buffer = "";
    let completed = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split(/\r?\n\r?\n/);
      buffer = parts.pop() || "";

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith("data:")) continue;
        let payload;
        try { payload = JSON.parse(line.slice(5).trim()); }
        catch { throw new Error("Invalid response event from the answer service"); }
        if (payload.type === "error") throw new Error(payload.message || payload.detail || "The answer service reported a streaming error");
        if (payload.type === "token" && payload.text) onToken?.(payload.text);
        if (payload.type === "sources") onSources?.(payload.sources);
        if (payload.type === "done") { completed = true; onDone?.(payload); }
      }
    }
    if (!completed) throw new Error("The answer stream ended before completion");
  },

  analytics: () => request("/analytics"),

  listApiKeys: () => request("/api-keys"),

  createApiKey: (name, expires_days = null) =>
    request("/api-keys", {
      method: "POST",
      body: JSON.stringify({ name, expires_days }),
    }),

  revokeApiKey: (key_id) =>
    request(`/api-keys/${encodeURIComponent(key_id)}`, { method: "DELETE" }),

  listIntegrations: () => request("/integrations"),

  connectIntegration: ({ catalog_id, name, env_keys_set = [], note = "" }) =>
    request("/integrations/connect", {
      method: "POST",
      body: JSON.stringify({ catalog_id, name, env_keys_set, note }),
    }),

  testIntegration: (catalog_id) =>
    request(`/integrations/${encodeURIComponent(catalog_id)}/test`, { method: "POST" }),

  disconnectIntegration: (catalog_id) =>
    request(`/integrations/${encodeURIComponent(catalog_id)}`, { method: "DELETE" }),

  uploadDocument: async ({ file, kb_id }) => {
    const form = new FormData();
    form.append("file", file);
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/documents/upload?kb_id=${encodeURIComponent(kb_id)}`, {
      method: "POST",
      headers,
      body: form,
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const body = await res.json();
        detail = apiErrorMessage(body, detail);
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    return res.json();
  },
};
